import { Pipe, PipeTransform } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Options accepted by {@link CloudinaryImgPipe}. Each maps 1:1 to the
 * Cloudinary transformation parameter the legacy `<cl-transformation>` /
 * `<cl-image>` attributes produced.
 */
export interface CloudinaryImgOptions {
  /** Output format, emitted as a file extension (e.g. 'png' -> `.png`). */
  format?: string;
  width?: string | number;
  height?: string | number;
  gravity?: string;
  crop?: string;
  quality?: string;
  /** Maps to `f_*` (e.g. 'auto' -> `f_auto`). Distinct from `format`. */
  fetchFormat?: string;
  dpr?: string | number;
}

/**
 * Replacement for the deprecated `@cloudinary/angular-5.x` `<cl-image>` /
 * `<cl-transformation>` directives (View-Engine only, unusable on Angular 16
 * after ngcc removal).
 *
 * Builds the delivery URL string directly so the output matches exactly what
 * the legacy SDK emitted:
 *   https://res.cloudinary.com/<cloud>/image/upload/<transforms>/<publicId>.<format>
 *
 * Usage: `<img [src]="publicId | cloudinaryImg:{ format: 'jpg', width: 78 }">`
 */
@Pipe({
  standalone: false, name: 'cloudinaryImg' })
export class CloudinaryImgPipe implements PipeTransform {
  private readonly cloudName =
    environment?.companyDetails?.config?.cludinaryCloud;

  transform(publicId: string, opts: CloudinaryImgOptions = {}): string {
    if (!publicId) {
      return '';
    }
    // Legacy public-ids are written with a leading slash (e.g. "/logo/37").
    // Cloudinary expects no leading slash; stripping it avoids a double slash.
    const id = String(publicId).replace(/^\/+/, '');

    const params: string[] = [];
    const push = (key: string, value: unknown) => {
      if (value !== undefined && value !== null && value !== '') {
        params.push(`${key}_${value}`);
      }
    };
    push('w', opts.width);
    push('h', opts.height);
    push('g', opts.gravity);
    push('c', opts.crop);
    push('q', opts.quality);
    push('f', opts.fetchFormat);
    push('dpr', opts.dpr);

    const transforms = params.length ? `${params.join(',')}/` : '';
    const ext = opts.format ? `.${opts.format}` : '';
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/${transforms}${id}${ext}`;
  }
}
